import React, { useState, useRef, useEffect, useContext } from 'react';
import '../../GridEditor/WaveProgressionTable.css';
import GameAssetsContext from '../../../../XXX/context/GameAssetContext';

// Types for backend wave data
export interface WaveData {
  waveNumber: number;
  level: number;
  gameTime: number;
  arrivalTiming: string;
  clearTiming: string;
  csGold: number;
  passiveGold: number;
  baseGold: number;
  scenarioGold: number;
  totalGold: number;
  goldSpentThisWave: number;
  goldRemaining: number;
  purchasedThisWave: PurchaseRecord[];
  inventory: InventoryItem[];
  bootsSlotItem?: InventoryItem; // 7th slot for Bot lane quest boots
  inventoryValue: number;
  currentTarget?: { id: string; name: string };
  // XP tracking
  cumulativeXP?: number;
  minionXP?: number;
  killXP?: number;
  // Quest tracking
  questPoints?: number;
  questCompleted?: boolean;
  questCompletedThisWave?: boolean;
  questBonusXP?: number;
  questBonusGold?: number;
}

export interface PurchaseRecord {
  id: string;
  name: string;
  payNow: number;
  totalCost: number;
  type: 'component' | 'intermediate' | 'final';
  rootId: string;
  rootName: string;
}

export interface InventoryItem {
  id: number;
  name: string;
  cost: number;
}

// Gold event types for events display
export interface GoldEvent {
  type: 'kill' | 'turret' | 'plate' | 'misc' | 'cspm' | 'quest';
  wave: number;
  champion: 'primary' | 'opponent';
  data: any;
}

// Selection manager interface
export interface WaveSelectionManager {
  isWaveSelected: (wave: number) => boolean;
  toggleWave: (wave: number) => { added: boolean; atLimit: boolean };
  getSelectedWaves: () => number[];
  clearSelection: () => void;
}

// Timeline view configurations (enhanced with filters)
const TIMELINE_VIEW_CONFIGS = [
  { id: 'all', name: 'All Waves', description: 'Show all calculated waves' },
  { id: 'early', name: 'Early Game', description: 'Waves 1-10' },
  { id: 'mid', name: 'Mid Game', description: 'Waves 11-20' },
  { id: 'late', name: 'Late Game', description: 'Waves 21+' },
  { id: 'levels', name: 'Level Ups', description: 'Show only waves where you level up' },
  { id: 'legendaries', name: 'Completed Items', description: 'Show waves when complete items are finished' },
  { id: 'components', name: 'Components', description: 'Show major components and completed items' },
  { id: 'items', name: 'All Items', description: 'Show any item acquisition' },
];

interface Props {
  // Wave data from backend
  primaryWaves: WaveData[];
  opponentWaves?: WaveData[];
  
  // Champion info
  primaryChampionName: string;
  opponentChampionName?: string;
  activeWaveChampion: 'primary' | 'opponent';
  onActiveWaveChampionChange?: (champion: 'primary' | 'opponent') => void;
  
  // Gold events for events column
  primaryGoldEvents?: any[];
  opponentGoldEvents?: any[];
  
  // Selection and export
  selectionManager?: WaveSelectionManager;
  existingGridWaves?: number[];
  onWaveExport?: (selectedWaves: number[]) => void;
  
  // Item display
  getItemImageUrl: (itemId: number) => string;
  
  // Styling
  className?: string;
  
  // Quest completion wave (from local calculation, used as fallback if backend data lacks quest fields)
  primaryQuestCompletionWave?: number | null;
  opponentQuestCompletionWave?: number | null;

  // Loading states
  loading?: boolean;
  error?: string;
}

const WaveProgressionTableV3: React.FC<Props> = ({
  primaryWaves = [],
  opponentWaves = [],
  primaryChampionName,
  opponentChampionName,
  activeWaveChampion = 'primary',
  onActiveWaveChampionChange,
  primaryGoldEvents = [],
  opponentGoldEvents = [],
  selectionManager,
  existingGridWaves = [],
  onWaveExport,
  getItemImageUrl,
  primaryQuestCompletionWave,
  opponentQuestCompletionWave,
  className = '',
  loading = false,
  error = null
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  
  // Local state
  const tableRef = useRef<HTMLDivElement>(null);
  const [visibleRows, setVisibleRows] = useState(10); // Show first 50 waves by default, expandable to show all backend provides
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
  const [selectedView, setSelectedView] = useState<string>('all');
  const [limitPulse, setLimitPulse] = useState<number | null>(null);
  
  // Get active waves based on champion selection
  const activeWaves = activeWaveChampion === 'primary' ? primaryWaves : opponentWaves;
  const activeGoldEvents = activeWaveChampion === 'primary' ? primaryGoldEvents : opponentGoldEvents;
  
  // Auto-scroll effect
  useEffect(() => {
    if (shouldScrollToBottom && tableRef.current) {
      tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      setShouldScrollToBottom(false);
    }
  }, [visibleRows, shouldScrollToBottom]);
  
  // Wave selection handler
  const handleToggleRow = (row: WaveData) => {
    if (!selectionManager) return;
    const { added, atLimit } = selectionManager.toggleWave(row.waveNumber);
    if (atLimit) {
      setLimitPulse(row.waveNumber);
      setTimeout(() => setLimitPulse(null), 400);
    }
  };
  
  // Filter waves based on selected view
  const getFilteredWaves = (waves: WaveData[]): WaveData[] => {
    switch (selectedView) {
      case 'early':
        return waves.filter(w => w.waveNumber <= 25);
      case 'mid':
        return waves.filter(w => w.waveNumber >= 26 && w.waveNumber <= 47);
      case 'late':
        return waves.filter(w => w.waveNumber >= 48);
      case 'levels':
        return waves.filter((wave, index) => {
          if (index === 0) return wave.level > 1;
          return wave.level > waves[index - 1].level;
        });
      case 'legendaries':
        // Show waves where completed items (final items) are purchased
        return waves.filter(wave => 
          wave.purchasedThisWave.some(purchase => purchase.type === 'final')
        );
      case 'components':
        // Show waves where components, intermediate items, or completed items are purchased
        return waves.filter(wave => 
          wave.purchasedThisWave.some(purchase => 
            ['intermediate', 'final'].includes(purchase.type)
          )
        );
      case 'items':
        return waves.filter(wave => wave.purchasedThisWave.length > 0);
      case 'all':
      default:
        return waves;
    }
  };
  
  // Get events for a specific wave (gold events + quest completion)
  const getEventsForWave = (waveNumber: number): GoldEvent[] => {
    const events: GoldEvent[] = [];
    
    // Gold events from parent
    if (activeGoldEvents) {
      activeGoldEvents.forEach(event => {
        if (event.wave === waveNumber) {
          events.push({
            type: event.type || 'misc',
            wave: waveNumber,
            champion: activeWaveChampion,
            data: event
          });
        }
      });
    }

    // Quest completion from wave data (backend) or local calculation (fallback)
    const activeWavesData = activeWaveChampion === 'primary' ? primaryWaves : opponentWaves;
    const waveData = activeWavesData?.find(w => w.waveNumber === waveNumber);
    const localQuestWave = activeWaveChampion === 'primary' ? primaryQuestCompletionWave : opponentQuestCompletionWave;
    const isQuestWaveFromBackend = waveData?.questCompletedThisWave;
    const isQuestWaveFromLocal = localQuestWave === waveNumber;

    if (isQuestWaveFromBackend || isQuestWaveFromLocal) {
      events.push({
        type: 'quest',
        wave: waveNumber,
        champion: activeWaveChampion,
        data: {
          questPoints: waveData?.questPoints,
          questBonusXP: waveData?.questBonusXP,
          questBonusGold: waveData?.questBonusGold
        }
      });
    }
    
    return events;
  };
  
  // Get displayed waves
  const filteredWaves = getFilteredWaves(activeWaves);
  const displayed = filteredWaves.slice(0, visibleRows);
  
 
  // Button handlers for expand controls
  const handlePlus = () => {
    const totalRows = filteredWaves.length;
    setVisibleRows(prev => Math.min(prev + 10, totalRows));
    setShouldScrollToBottom(true);
  };

  const handleMinus = () => {
    setVisibleRows(prev => Math.max(prev - 10, 0));
  };

  const handleShowAll = () => {
    setVisibleRows(filteredWaves.length);
  };

  const handleContractAll = () => {
    setVisibleRows(0);
  };
  
  // Loading state
  if (loading) {
    return (
      <div className={`wpt-wave-progression-table ${className}`}>
        <div className="loading-message">
          <div className="loading-spinner"></div>
          <span>Calculating wave progression...</span>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className={`wpt-wave-progression-table ${className}`}>
        <div className="wpt-error-state">
          <p>Error loading wave progression: {error}</p>
        </div>
      </div>
    );
  }
  
  // Empty state
  if (activeWaves.length === 0) {
    return (
      <div className={`wpt-wave-progression-table ${className}`}>
        <div className="wpt-empty-state">
          <p>No wave data available. Configure gold settings and select items to see wave progression.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`wpt-wave-progression-table ${className}`} ref={tableRef}>
      
      {/* Timeline View Selector */}
      <div className="wpt-timeline-view-selector">
        <div className="wpt-controls-row">
          {/* Wave view buttons on the left */}
          <div className="wpt-view-buttons">
            {TIMELINE_VIEW_CONFIGS.map(config => (
              <button
                key={config.id}
                className={`wpt-view-btn ${selectedView === config.id ? 'active' : ''}`}
                onClick={() => {
                  setSelectedView(config.id);
                  setVisibleRows(10);
                }}
                title={config.description}
              >
                {config.name}
              </button>
            ))}
          </div>
          
          {/* Champion controls on the right */}
          {(primaryChampionName || opponentChampionName) && (
            <div className="wpt-champion-controls">
              {primaryChampionName && (
                <button
                  className={`wpt-champion-btn ${activeWaveChampion === 'primary' ? 'active' : ''}`}
                  onClick={() => onActiveWaveChampionChange?.('primary')}
                >
                  {primaryChampionName}
                </button>
              )}
              {opponentChampionName && (
                <button
                  className={`wpt-champion-btn ${activeWaveChampion === 'opponent' ? 'active' : ''}`}
                  onClick={() => onActiveWaveChampionChange?.('opponent')}
                >
                  {opponentChampionName}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Table Header - Exact same as V2 */}
      <div className="wpt-wave-prog-table-header">
        <span>Wave</span>
        <span>Arrives</span>
        <span>Cleared</span>
        <span>Level</span>
        <span>Events</span>
        <span>Base Gold</span>
        <span>Total Gold</span>
        <span>Inventory Value</span>
        <span>Gold Remaining</span>
        <span>Purchased This Wave</span>
        <span>Inventory</span>
        <span>Export</span>
      </div>
      
      {/* Table Rows */}
      {displayed.map((row, index) => {
        const isSelectedForExport = selectionManager?.isWaveSelected(row.waveNumber) ?? false;
        const isExistingInGrid = existingGridWaves.includes(row.waveNumber);
        
        const previousRow = index > 0 ? displayed[index - 1] : undefined;
        const isLevelUp = previousRow ? row.level > previousRow.level : row.level > 1;
        
        // Determine the selection class
        let selectionClass = '';
        if (isSelectedForExport) {
          if (isExistingInGrid) {
            selectionClass = 'wpt-wave-selected'; // existing selection (light blue)
          } else {
            selectionClass = 'wpt-wave-selected-new'; // new selection (darker blue)
          }
        }
        
        return (
          <div
            key={row.waveNumber}
            className={
              `wpt-wave-prog-row ${selectionClass}` +
              (limitPulse === row.waveNumber ? ' wpt-wave-limit-pulse' : '')
            }
            onClick={(e) => {
              // don't double-toggle if they clicked directly on the checkbox
              const target = e.target as HTMLElement;
              if (target.closest('.wpt-checkbox-input')) return;
              handleToggleRow(row);
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggleRow(row);
              }
            }}
          >
            {/* Wave Number */}
            <span className="wpt-wave-num">{row.waveNumber}</span>
            
            {/* Arrives */}
            <span className="wpt-wave-arrival-timing">{row.arrivalTiming}</span>
            
            {/* Cleared */}
            <span className="wpt-wave-clear-timing">{row.clearTiming}</span>
            
            {/* Level */}
            <span className="wpt-wave-level">
              {row.level}
              {isLevelUp && <span className="wpt-level-up-arrow">↗</span>}
            </span>
            
            {/* Events column */}
            <div className="wpt-wave-events">
              {(() => {
                const events = getEventsForWave(row.waveNumber);
                
                if (events.length === 0) {
                  return <span className="wpt-no-events">—</span>;
                }
                
                return events.map((event, eventIndex) => {
                  const getEventIcon = () => {
                    switch (event.type) {
                      case 'kill':
                        return gameWideAssets?.damageIconWhite ? (
                          <img src={gameWideAssets.damageIconWhite} alt="Kill" />
                        ) : '⚔';
                      case 'turret':
                        return gameWideAssets?.turretIcon ? (
                          <img src={gameWideAssets.turretIcon} alt="Turret" />
                        ) : '🏰';
                      case 'plate':
                        return gameWideAssets?.turretPlateIcon ? (
                          <img src={gameWideAssets.turretPlateIcon} alt="Plate" />
                        ) : '🛡';
                      case 'quest':
                        return <span className="wpt-quest-icon" title="Role Quest Completed">R</span>;
                      case 'misc':
                        return '?';
                      default:
                        return '•';
                    }
                  };
                  
                  const getValue = () => {
                    if (event.type === 'quest') {
                      // Quest event - show XP/gold bonuses
                      const parts: string[] = [];
                      if (event.data.questBonusXP) parts.push(`+${event.data.questBonusXP} XP`);
                      if (event.data.questBonusGold) parts.push(`+${event.data.questBonusGold}g`);
                      return parts.join(', ') || 'Quest Complete';
                    }
                    if (event.data.value !== undefined) {
                      return event.data.value;
                    } else if (event.data.localGold !== undefined && event.data.globalGold !== undefined) {
                      return event.data.localGold + event.data.globalGold;
                    } else if (event.data.goldValue !== undefined) {
                      return event.data.goldValue;
                    }
                    return 0;
                  };
                  
                  return (
                    <div 
                      key={`${event.type}-${event.wave}-${eventIndex}`}
                      className={`wpt-event-icon ${event.champion} ${event.type === 'misc' ? 'misc' : ''} ${event.type === 'quest' ? 'quest' : ''}`}
                      title={event.type === 'quest' ? `Role Quest Complete - ${getValue()}` : `${event.type} - ${getValue()}g`}
                    >
                      {getEventIcon()}
                    </div>
                  );
                });
              })()}
            </div>
            
            {/* Base Gold */}
            <span className="wpt-base-gold">
              {row.baseGold}g
              {row.scenarioGold !== 0 && (
                <span className={`wpt-scenario-addition ${row.scenarioGold >= 0 ? 'wpt-positive' : 'wpt-negative'}`}>
                  {row.scenarioGold >= 0 ? '+' : ''}{row.scenarioGold}g
                </span>
              )}
            </span>
            
            {/* Total Gold */}
            <span className="wpt-total-gold">{row.totalGold}g</span>
            
            {/* Inventory Value */}
            <span className="wpt-inventory-value">{row.inventoryValue}g</span>
            
            {/* Gold Remaining */}
            <span className="wpt-total-gold">{row.goldRemaining}g</span>
            
            {/* Purchased this wave */}
            <span className="wpt-next-purchase-compact">
              {row.purchasedThisWave.length ? (
                <div className="wpt-enhanced-next-purchase">
                  {row.purchasedThisWave.map((p, i) => (
                    <div key={`${row.waveNumber}-p-${i}`} className="wpt-next-component" title={`${p.name} (${p.type})`}>
                      <div className="wpt-next-item-image">
                        <img
                          src={getItemImageUrl(Number(p.id))}
                          alt={p.name}
                          className="wpt-next-item-img"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.textContent = p.name.length > 3 ? p.name.substring(0, 3) : p.name;
                              parent.classList.add('wpt-compact-item-fallback');
                            }
                          }}
                        />
                      </div>
                      <div className="wpt-next-cost">
                        <span className="wpt-can-afford">{p.payNow}g</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <span className="wpt-build-complete">—</span>
              )}
            </span>
            
            {/* Inventory */}
            <span className="wpt-compact-inventory">
              {(() => {
                // Combine main inventory + boots slot item (if present) into a flat list
                const allItems = [...row.inventory];
                if (row.bootsSlotItem) {
                  allItems.push(row.bootsSlotItem);
                }
                const hasBootsSlot = !!row.bootsSlotItem;
                
                const top = allItems.slice(0, hasBootsSlot ? 4 : 3);
                const bottom = allItems.slice(hasBootsSlot ? 4 : 3, hasBootsSlot ? 7 : 6);
                
                // pad rows so layout is stable
                const padTo = <T,>(arr: T[], count: number) => [...arr, ...Array(Math.max(0, count - arr.length)).fill(null)];
                
                const renderSlot = (it: typeof row.inventory[number] | null, idx: number, keyPrefix: string) =>
                  it ? (
                    <div 
                      key={`${keyPrefix}-${idx}`} 
                      className="wpt-compact-item"
                      title={`${it.name} (${it.cost}g)`}
                    >
                      <img
                        src={getItemImageUrl(it.id)}
                        alt={it.name}
                        className="wpt-compact-item-image"
                        onError={(e) => {
                          const target = e.currentTarget as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.textContent = it.name.length > 3 ? it.name.substring(0, 3) : it.name;
                            parent.classList.add('wpt-compact-item-fallback');
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div key={`${keyPrefix}-${idx}`} className="wpt-compact-item wpt-empty-slot" />
                  );
                
                return (
                  <>
                    <div className="wpt-inv-row">
                      {padTo(top, hasBootsSlot ? 4 : 3).map((it, i) => renderSlot(it as any, i, `${row.waveNumber}-inv-top`))}
                    </div>
                    <div className="wpt-inv-row">
                      {padTo(bottom, 3).map((it, i) => renderSlot(it as any, i, `${row.waveNumber}-inv-bot`))}
                    </div>
                  </>
                );
              })()}
            </span>
            
            {/* Export checkbox */}
            <div className="wpt-wave-checkbox">
              <input
                type="checkbox"
                className="wpt-checkbox-input"
                checked={isSelectedForExport}
                onChange={() => handleToggleRow(row)}
                disabled={!selectionManager}
              />
              <div className="wpt-checkbox-custom"></div>
            </div>
            
          </div>
        );
      })}
      
      {/* Expand Controls */}
      {(displayed.length < filteredWaves.length || displayed.length > 0) && (
        <div className="wpt-expand-controls">
          <div className="wpt-showing-info">
            Showing {displayed.length} of {filteredWaves.length} waves
            {selectedView !== 'all' && ` (${activeWaves.length} total)`}
          </div>
          
          <div className="wpt-selection-controls">
            {selectionManager && (
              <>
                <button 
                  className="wpt-selection-btn wpt-select-btn" 
                  onClick={() => {
                    displayed.forEach(wave => {
                      if (!selectionManager.isWaveSelected(wave.waveNumber)) {
                        selectionManager.toggleWave(wave.waveNumber);
                      }
                    });
                  }}
                  disabled={displayed.every(wave => selectionManager.isWaveSelected(wave.waveNumber))}
                >
                  Select Visible ({displayed.length})
                </button>
                <button 
                  className="wpt-selection-btn wpt-clear-btn" 
                  onClick={() => selectionManager.clearSelection()}
                  disabled={selectionManager.getSelectedWaves().length === 0}
                >
                  Clear All
                </button>
                {selectionManager.getSelectedWaves().length > 0 && (
                  <span className="wpt-selection-count">
                    {selectionManager.getSelectedWaves().length} selected
                  </span>
                )}
              </>
            )}
            
       
          </div>
          
          <div className="wpt-expand-buttons">
            <button 
              className="wpt-expand-btn wpt-minus-btn" 
              onClick={handleMinus} 
              disabled={visibleRows <= 0} 
              title="Show 10 fewer rows"
            >
              <span className="wpt-button-icon">−</span>
            </button>
            <button 
              className="wpt-expand-btn wpt-plus-btn" 
              onClick={handlePlus} 
              disabled={visibleRows >= filteredWaves.length} 
              title="Show 10 more rows"
            >
              <span className="wpt-button-icon">+</span>
            </button>
            <button 
              className="wpt-expand-btn wpt-contract-all-btn" 
              onClick={handleContractAll} 
              disabled={visibleRows <= 0} 
              title="Hide all rows"
            >
              <span className="wpt-arrow-up">▲</span>
            </button>
            <button 
              className="wpt-expand-btn wpt-show-all-btn" 
              onClick={handleShowAll} 
              disabled={visibleRows >= filteredWaves.length} 
              title="Show all rows"
            >
              <span className="wpt-arrow-down">▼</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Export Button */}
   {/*    {selectionManager && selectionManager.getSelectedWaves().length > 0 && (
        <div className="wpt-export-container">
          <button 
            className="wpt-export-btn"
            onClick={() => onWaveExport?.(selectionManager.getSelectedWaves())}
          >
            Export Selected Waves ({selectionManager.getSelectedWaves().length})
          </button>
        </div>
      )} */}
      
    </div>
  );
};

export default WaveProgressionTableV3;
